import { PAYMONGO_API_URL, type PaymentMethodKind } from "@sagot/constants";

/** Card details stay in the browser: the PSP tokenizes them and the API only sees the token. */
export interface CardInput {
  number: string;
  expMonth: number;
  expYear: number;
  cvc: string;
}

export interface TokenizeInput {
  type: PaymentMethodKind;
  card?: CardInput;
  /** GCash / Maya wallet number (only its last 4 digits are kept). */
  mobile?: string;
}

export interface TokenizedMethod {
  providerMethodId: string;
  brand: string | null;
  last4: string | null;
  expMonth: number | null;
  expYear: number | null;
}

export interface PaymentTokenizer {
  readonly mode: "paymongo" | "mock";
  tokenize: (input: TokenizeInput) => Promise<TokenizedMethod>;
}

const BANK_METHOD_ID = "manual_bank_transfer";
/** PayMongo's type names for the wallets. */
const PAYMONGO_TYPES: Record<Exclude<PaymentMethodKind, "bank">, string> = { card: "card", gcash: "gcash", maya: "paymaya" };

export function digitsOnly(value: string): string {
  return value.replace(/\D/g, "");
}

/** Card network from the leading digits (display only). */
export function cardBrand(number: string): string {
  const digits = digitsOnly(number);
  if (digits.startsWith("4")) return "visa";
  if (/^(5[1-5]|2[2-7])/.test(digits)) return "mastercard";
  if (/^3[47]/.test(digits)) return "amex";
  if (digits.startsWith("35")) return "jcb";
  return "card";
}

/** Luhn checksum for client-side card number validation. */
export function luhnValid(number: string): boolean {
  const digits = digitsOnly(number);
  if (digits.length < 12 || digits.length > 19) return false;
  let sum = 0;
  for (let i = 0; i < digits.length; i += 1) {
    let digit = Number(digits[digits.length - 1 - i]);
    if (i % 2 === 1) {
      digit *= 2;
      if (digit > 9) digit -= 9;
    }
    sum += digit;
  }
  return sum % 10 === 0;
}

function describe(input: TokenizeInput): Omit<TokenizedMethod, "providerMethodId"> {
  if (input.type === "card" && input.card) {
    return { brand: cardBrand(input.card.number), last4: digitsOnly(input.card.number).slice(-4), expMonth: input.card.expMonth, expYear: input.card.expYear };
  }
  const last4 = input.mobile ? digitsOnly(input.mobile).slice(-4) || null : null;
  return { brand: input.type === "bank" ? null : input.type, last4, expMonth: null, expYear: null };
}

function randomId(): string {
  const bytes = new Uint8Array(12);
  crypto.getRandomValues(bytes);
  return Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
}

/** Development tokenizer (MockPayments backend): never sends card data anywhere. */
export function createMockTokenizer(): PaymentTokenizer {
  return {
    mode: "mock",
    tokenize: (input) => {
      const providerMethodId = input.type === "bank" ? BANK_METHOD_ID : `pm_mock_${input.type}_${randomId()}`;
      return Promise.resolve({ providerMethodId, ...describe(input) });
    },
  };
}

interface PaymongoMethodResponse {
  data?: { id?: string };
}

/** PayMongo public-key tokenization (`POST /v1/payment_methods` with the publishable key). */
export function createPaymongoTokenizer(publicKey: string, fetcher: typeof fetch = fetch): PaymentTokenizer {
  return {
    mode: "paymongo",
    tokenize: async (input) => {
      if (input.type === "bank") return { providerMethodId: BANK_METHOD_ID, ...describe(input) };
      const details = input.type === "card" && input.card
        ? { card_number: digitsOnly(input.card.number), exp_month: input.card.expMonth, exp_year: input.card.expYear, cvc: input.card.cvc }
        : undefined;
      const response = await fetcher(`${PAYMONGO_API_URL}/payment_methods`, {
        method: "POST",
        headers: { "content-type": "application/json", authorization: `Basic ${btoa(`${publicKey}:`)}` },
        body: JSON.stringify({ data: { attributes: { type: PAYMONGO_TYPES[input.type], ...(details ? { details } : {}) } } }),
      });
      if (!response.ok) throw new Error("tokenization_failed");
      const body = (await response.json()) as PaymongoMethodResponse;
      const id = body.data?.id;
      if (!id) throw new Error("tokenization_failed");
      return { providerMethodId: id, ...describe(input) };
    },
  };
}

/** The PayMongo entry of `GET /v1/billing/config`: its mode and the active mode's public key. */
export interface PaymongoGatewayConfig {
  mode: string;
  public_key: string | null;
}

/**
 * The browser tokenizer for the server's PayMongo mode (DEC-065). The key and mode come from the
 * billing config, never a build-time variable, so the browser always tokenizes against the same
 * PayMongo mode the server charges in. `mock` mode, or no key, falls back to the local mock.
 */
export function tokenizerFor(config: PaymongoGatewayConfig | undefined, fetcher: typeof fetch = fetch): PaymentTokenizer {
  if (!config || config.mode === "mock" || !config.public_key) return createMockTokenizer();
  return createPaymongoTokenizer(config.public_key, fetcher);
}
